import { OrderCondition } from "@/gql/gen";

/**
 * Поля OrderCondition, которые можно исключить или очистить
 */
export type OrderConditionField = 
    | 'BENEFIT_TYPE'
    | 'OFFICE'
    | 'OFFICE_ROLE'
    | 'PAYMENT_TYPE'
    | 'TRANSPORT_TYPE'
    | 'ORDER_PRICE_FROM'
    | 'PRIZE'
    | 'COUPON'
    | 'PRODUCT'
    | 'PRODUCT_SECTION';

/**
 * Создает копию OrderCondition с исключенными или очищенными полями
 * 
 * @param condition - Исходное условие
 * @param excludeFields - Массив полей для исключения (будут заменены на пустые массивы или null)
 * @returns Новый объект OrderCondition с исключенными полями
 * 
 * @example
 * // Исключить условия по товарам и секциям
 * const filtered = filterOrderCondition(condition, ['PRODUCT', 'PRODUCT_SECTION'])
 * 
 * @example
 * // Исключить BENEFIT_TYPE
 * const filtered = filterOrderCondition(condition, ['BENEFIT_TYPE'])
 */
export function filterOrderCondition(
    condition: OrderCondition,
    excludeFields: OrderConditionField[]
): OrderCondition {
    const result: OrderCondition = {
        __typename: condition.__typename,
        BENEFIT_TYPE: condition.BENEFIT_TYPE,
        OFFICE: condition.OFFICE,
        OFFICE_ROLE: condition.OFFICE_ROLE,
        PAYMENT_TYPE: condition.PAYMENT_TYPE,
        TRANSPORT_TYPE: condition.TRANSPORT_TYPE,
        ORDER_PRICE_FROM: condition.ORDER_PRICE_FROM,
        PRIZE: condition.PRIZE,
        COUPON: condition.COUPON,
        PRODUCT: condition.PRODUCT,
        PRODUCT_SECTION: condition.PRODUCT_SECTION
    };

    // Очищаем исключенные поля
    excludeFields.forEach(field => {
        switch (field) {
            case 'BENEFIT_TYPE':
            case 'OFFICE':
            case 'OFFICE_ROLE':
            case 'PAYMENT_TYPE':
            case 'TRANSPORT_TYPE':
            case 'PRODUCT':
            case 'PRODUCT_SECTION':
                // Массивы заменяем на пустые массивы
                result[field] = [] as any;
                break;
            case 'ORDER_PRICE_FROM':
            case 'PRIZE':
            case 'COUPON':
                // Nullable поля заменяем на null
                result[field] = null;
                break;
        }
    });

    return result;
}

/**
 * Создает копию OrderCondition с переопределенными полями
 * 
 * @param condition - Исходное условие
 * @param overrides - Объект с полями для переопределения
 * @returns Новый объект OrderCondition с переопределенными полями
 * 
 * @example
 * // Переопределить BENEFIT_TYPE
 * const modified = overrideOrderCondition(condition, { BENEFIT_TYPE: ['discount'] })
 * 
 * @example
 * // Очистить товары и секции, добавить BENEFIT_TYPE
 * const modified = overrideOrderCondition(condition, {
 *   BENEFIT_TYPE: ['discount'],
 *   PRODUCT: [],
 *   PRODUCT_SECTION: []
 * })
 */
export function overrideOrderCondition(
    condition: OrderCondition,
    overrides: Partial<OrderCondition>
): OrderCondition {
    return {
        ...condition,
        ...overrides
    };
}
